import React, { useState } from 'react';
import Modal from '../common/Modal';
import { ClassData } from '../../types';

interface ManualRegistrationModalProps {
    isOpen: boolean;
    onClose: () => void;
    classData: ClassData | null;
    onRegister: (firstName: string, lastName: string, email: string, paymentMethod: 'arrangement' | 'venmo' | 'none', skipConfirmation: boolean) => Promise<void>;
}

const ManualRegistrationModal: React.FC<ManualRegistrationModalProps> = ({ isOpen, onClose, classData, onRegister }) => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [paymentMethod, setPaymentMethod] = useState<'arrangement' | 'venmo' | 'none'>('arrangement');
    const [skipConfirmation, setSkipConfirmation] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!classData) return;

        setIsLoading(true);
        try {
            await onRegister(firstName, lastName, email, paymentMethod, skipConfirmation);
            // Reset form
            setFirstName('');
            setLastName('');
            setEmail('');
            setPaymentMethod('arrangement');
            setSkipConfirmation(false);
            onClose();
        } catch (error) {
            console.error('Registration error:', error);
            // Error is handled by the parent component via onRegister callback
        } finally {
            setIsLoading(false);
        }
    };

    if (!classData) return null;

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`Register for ${classData.name}`}>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="bg-slate-800 p-3 rounded mb-4 border border-slate-700">
                     <p className="text-sm text-slate-400">Class Details:</p>
                     <p className="font-bold text-slate-200">{classData.name}</p>
                     <p className="text-sm text-slate-300">{classData.date} @ {classData.startTime}</p>
                </div>

                <div>
                    <label className="block text-sm font-medium text-slate-400 mb-1">First Name</label>
                    <input 
                        type="text" 
                        required
                        className="w-full bg-slate-800 border-none rounded p-2 text-slate-200 focus:ring-2 focus:ring-fuchsia-500"
                        value={firstName}
                        onChange={e => setFirstName(e.target.value)}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-slate-400 mb-1">Last Name</label>
                    <input 
                        type="text" 
                        required
                        className="w-full bg-slate-800 border-none rounded p-2 text-slate-200 focus:ring-2 focus:ring-fuchsia-500"
                        value={lastName}
                        onChange={e => setLastName(e.target.value)}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-slate-400 mb-1">Email</label>
                    <input 
                        type="email" 
                        required
                        className="w-full bg-slate-800 border-none rounded p-2 text-slate-200 focus:ring-2 focus:ring-fuchsia-500"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                    />
                </div>
                
                <div>
                    <label className="block text-sm font-medium text-slate-400 mb-1">Payment Status</label>
                    <select 
                        className="w-full bg-slate-800 border-none rounded p-2 text-slate-200 focus:ring-2 focus:ring-fuchsia-500"
                        value={paymentMethod}
                        onChange={e => setPaymentMethod(e.target.value as any)}
                    >
                        <option value="arrangement">Arrangement (Pay Later/Other)</option>
                        <option value="none">Cash (Paid)</option>
                        <option value="venmo">Venmo (Paid)</option>
                    </select>
                    <p className="text-xs text-slate-500 mt-1">
                        "Arrangement" will mark as pending. Cash/Venmo will mark as paid.
                    </p>
                </div>

                <div className="flex items-center gap-2 pt-2">
                    <input 
                        type="checkbox"
                        id="skipConfirmation"
                        className="rounded bg-slate-800 border-slate-600 text-fuchsia-600 focus:ring-fuchsia-500"
                        checked={skipConfirmation}
                        onChange={e => setSkipConfirmation(e.target.checked)}
                    />
                    <label htmlFor="skipConfirmation" className="text-sm text-slate-300">
                        Skip registration confirmation email
                    </label>
                </div>

                <div className="pt-4 flex justify-end gap-2">
                    <button 
                        type="button" 
                        onClick={onClose}
                        className="px-4 py-2 rounded text-slate-300 hover:bg-slate-800 transition-colors"
                    >
                        Cancel
                    </button>
                    <button 
                        type="submit" 
                        disabled={isLoading}
                        className="px-4 py-2 rounded bg-fuchsia-600 text-white hover:bg-fuchsia-700 transition-colors disabled:opacity-50"
                    >
                        {isLoading ? 'Registering...' : 'Register Student'}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default ManualRegistrationModal;
